import React, { useContext, useEffect, useState } from 'react';
import DepartmentContext from '../../contexts/DepartmentContext';
import ConfirmDialog from '../ConfirmDialog';
import WeekPicker from '../ui/WeekPicker';
import ChecklistDropdown from '../ui/ChecklistDropdown';
import { FIELD_LABEL_CLASS } from '../../constants/inputs';
import { addDays, daysDiff, parseDate, toDateStr } from '../../utils/ganttDates';
import { formatDate } from '../../utils/dateFormat';
import { CSV_LABEL } from '../../utils/gagCsvExport';
import { CREW_HOURS_ONLY_TEXT, EXPORT_SECTIONS, GROUPED_BY_TEXT } from '../../utils/timesheetExport';

/**
 * "Export timesheets…" — ONE dialog for every export (owner-approved mock
 * `_wip/1028/mock-ts-export.html`, 2026-10-28): Dates · People · Include · Format.
 *
 * Built on `components/ConfirmDialog`'s `children` slot (house rule: no fifth
 * modal shell). 📐 That shell is `max-w-md` (448px) where the mock drew 620px, and on
 * desktop it does NOT scroll (only below `md`), so the body is COMPACT by design: each
 * group's choices sit on ONE wrapping row, hints are short (the long ones are the
 * rows' `title`). Measured 2026-10-28 in the real build: the first, mock-literal
 * layout was 934px tall with a range open and lost its title AND its Export button
 * at 1366×768. Do not grow it back without measuring at 1366×768.
 *
 *  - Dates: this week / last week / a range of WHOLE weeks (two `ui/WeekPicker`s,
 *    Mon–Sun), at most `MAX_EXPORT_WEEKS` — the server refuses more too.
 *  - People: everyone / chosen HOME sub-departments / chosen people
 *    (`ui/ChecklistDropdown`). Grouping is by home sub-department — said here.
 *  - Include: the three sections, and the amber "Projected" tick, DEFAULT OFF
 *    (owner ruling C: off = submitted weeks only; outstanding sheets listed by
 *    name with no money).
 *  - Format: CSV (one `.csv`, titled section blocks — ruling A; `CSV_LABEL`) / PDF (A4 landscape).
 *
 * It decides nothing about authority (the menu is on the manager screen; the
 * server gates `canManageBudget`) and computes no money. `onExport(request)`
 * gets `{ from, to, unitIds, lineageIds, includeProjected, sections, format }`.
 *
 * 🧑‍🔧 `crew` (owner 2026-11-01, "My timesheet" — HOURS ONLY): "Export my timesheets", the SAME
 * Dates (this / last / a range, the same `exportRequestProblem` cap and order) and Format, no
 * People / Include / Projected; PDF first. `onExport` gets `{ from, to, format }` only.
 */
export const MAX_EXPORT_WEEKS = 13;

// The radio / checkbox look already used twice under `components/hires/` (private there too).
const CHOICE = 'h-4 w-4 shrink-0 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500';
const OPTION = 'flex items-center gap-2 py-1 min-h-[44px] md:min-h-0 text-sm text-gray-800 dark:text-gray-200';
const ROW = 'flex flex-wrap items-center gap-x-4';
const HINT = 'text-xs text-gray-500 dark:text-gray-400';

const shiftWeeks = (monday, n) => toDateStr(addDays(parseDate(monday), n * 7));
const weekCount = (from, to) => Math.floor(daysDiff(parseDate(from), parseDate(to)) / 7) + 1;

/** The dialog's own validation — what keeps Export disabled, in words. */
export function exportRequestProblem(s) {
  if (s.dates === 'range') {
    if (s.to < s.from) return 'The last week is before the first.';
    if (weekCount(s.from, s.to) > MAX_EXPORT_WEEKS) return `Choose at most ${MAX_EXPORT_WEEKS} weeks.`;
  }
  if (s.people === 'subs' && s.subIds.length === 0) return 'Choose at least one sub-department.';
  if (s.people === 'chosen' && s.lineageIds.length === 0) return 'Choose at least one person.';
  if (s.sections.length === 0) return 'Tick at least one thing to include.';
  return null;
}

/** The state → the request the menu sends. */
export function exportRequestFrom(s, weekStart) {
  let from = weekStart;
  let to = weekStart;
  if (s.dates === 'last') { from = shiftWeeks(weekStart, -1); to = from; }
  if (s.dates === 'range') { from = s.from; to = s.to; }
  return {
    from,
    to,
    unitIds: s.people === 'subs' ? s.subIds.map(Number) : null,
    lineageIds: s.people === 'chosen' ? [...s.lineageIds] : null,
    includeProjected: !!s.includeProjected,
    sections: EXPORT_SECTIONS.map((x) => x.id).filter((id) => s.sections.includes(id)),
    format: s.format,
  };
}

const initialState = (weekStart, crew = false) => ({
  dates: 'this',
  from: shiftWeeks(weekStart, -3),
  to: weekStart,
  people: 'everyone',
  subIds: [],
  lineageIds: [],
  // The mock's defaults: Pay summary + overview ticked, detail not, Projected OFF.
  sections: ['overview', 'summary'],
  includeProjected: false,
  format: crew ? 'pdf' : 'csv',
});

/** One radio in an inline row of choices (the row wraps at 390px). */
function Radio({ name, value, current, onPick, testId, children }) {
  return (
    <label className={OPTION}>
      <input type="radio" name={name} value={value} checked={current === value} onChange={() => onPick(value)}
        className={CHOICE} data-testid={testId} />
      <span>{children}</span>
    </label>
  );
}

function Section({ legend, children }) {
  return (
    <fieldset className="py-2">
      <legend className={FIELD_LABEL_CLASS}>{legend}</legend>
      {children}
    </fieldset>
  );
}

export const CREW_EXPORT_TITLE = 'Export my timesheets';

export default function TimesheetExportDialog({
  open, weekStart, subtitle = null, units = [], people = [], busy = false, error = null, onCancel, onExport, crew = false,
}) {
  // The subtitle ("SFX · Project Aurora"): read WITHOUT the throwing hook, so the dialog renders in any tree.
  const deptCtx = useContext(DepartmentContext);
  const dept = deptCtx && deptCtx.activeDepartment;
  const heading = subtitle || (dept ? [dept.department_name, dept.production_name].filter(Boolean).join(' · ') : null) || null;
  const [s, setS] = useState(() => initialState(weekStart, crew));
  // Every opening starts from the screen's week (a stale range from last time is a trap).
  useEffect(() => { if (open) setS(initialState(weekStart, crew)); }, [open, weekStart, crew]);
  const set = (patch) => setS((cur) => ({ ...cur, ...patch }));
  const problem = exportRequestProblem(s);
  const toggleSection = (id) => set({ sections: s.sections.includes(id) ? s.sections.filter((x) => x !== id) : [...s.sections, id] });
  const unitItems = units.map((u) => ({ id: Number(u.id), label: u.name }));
  const personItems = [...people].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    .map((p) => ({ id: p.lineage_id, label: p.name }));
  const n = weekCount(s.from, s.to);
  const pdfChoice = (
    <Radio name="ts-export-format" value="pdf" current={s.format} onPick={(v) => set({ format: v })} testId="ts-export-format-pdf">
      PDF <span className={HINT}>{crew ? 'A4 landscape, one page per week' : 'A4 landscape'}</span>
    </Radio>
  );
  const datesHint = s.dates === 'this' ? `w/c ${formatDate(weekStart)}`
    : s.dates === 'last' ? `w/c ${formatDate(shiftWeeks(weekStart, -1))}` : null;

  return (
    <ConfirmDialog
      open={open}
      title={crew ? CREW_EXPORT_TITLE : 'Export timesheets'}
      message={crew ? CREW_HOURS_ONLY_TEXT : heading}
      confirmLabel={busy ? 'Exporting…' : 'Export'}
      busy={busy}
      confirmDisabled={!!problem}
      onCancel={onCancel}
      onConfirm={() => {
        if (problem) return;
        const r = exportRequestFrom(s, weekStart);
        onExport(crew ? { from: r.from, to: r.to, format: r.format } : r);
      }}
      initialFocus="cancel"
    >
      <div data-testid="ts-export-dialog" className="mb-3 divide-y divide-gray-200 border-y border-gray-200 text-left dark:divide-gray-700 dark:border-gray-700">
        <Section legend="Dates">
          <div className={ROW}>
            <Radio name="ts-export-dates" value="this" current={s.dates} onPick={(v) => set({ dates: v })} testId="ts-export-dates-this">This week</Radio>
            <Radio name="ts-export-dates" value="last" current={s.dates} onPick={(v) => set({ dates: v })} testId="ts-export-dates-last">Last week</Radio>
            <Radio name="ts-export-dates" value="range" current={s.dates} onPick={(v) => set({ dates: v })} testId="ts-export-dates-range">Weeks from…to</Radio>
          </div>
          {datesHint && <p className={HINT} data-testid="ts-export-dates-hint">{datesHint}, Mon–Sun</p>}
          {s.dates === 'range' && (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="ts-export-range">
              <WeekPicker weekStart={s.from} onChange={(w) => set({ from: w })} idPrefix="ts-export-from" ariaLabel="First week" />
              <span className="text-sm text-gray-700 dark:text-gray-300">to</span>
              <WeekPicker weekStart={s.to} onChange={(w) => set({ to: w })} idPrefix="ts-export-to" ariaLabel="Last week" />
              <span className={HINT} data-testid="ts-export-range-count">{n > 0 ? `${n} ${n === 1 ? 'week' : 'weeks'}, Mon–Sun` : ''}</span>
            </div>
          )}
        </Section>

        {!crew && (<>
        <Section legend="People">
          <div className={ROW}>
            <Radio name="ts-export-people" value="everyone" current={s.people} onPick={(v) => set({ people: v })} testId="ts-export-people-everyone">
              Everyone <span className={HINT}>({people.length})</span>
            </Radio>
            <Radio name="ts-export-people" value="subs" current={s.people} onPick={(v) => set({ people: v })} testId="ts-export-people-subs">Sub-departments</Radio>
            <Radio name="ts-export-people" value="chosen" current={s.people} onPick={(v) => set({ people: v })} testId="ts-export-people-chosen">Chosen people</Radio>
          </div>
          {s.people === 'subs' && (
            <div className="mt-1">
              <ChecklistDropdown testId="ts-export-subs" ariaLabel="Sub-departments" placeholder="Choose sub-departments…"
                sections={[{ key: 'subs', items: unitItems }]} selected={s.subIds}
                onChange={(ids) => set({ subIds: ids.map(Number) })} fullWidth />
            </div>
          )}
          {s.people === 'chosen' && (
            <div className="mt-1">
              <ChecklistDropdown testId="ts-export-people" ariaLabel="People" placeholder="Pick people…"
                sections={[{ key: 'people', items: personItems }]} selected={s.lineageIds}
                onChange={(ids) => set({ lineageIds: ids })} fullWidth />
            </div>
          )}
          <p className={`${HINT} mt-1`}>{GROUPED_BY_TEXT}.</p>
        </Section>

        <Section legend="Include">
          {EXPORT_SECTIONS.map((sec) => (
            <label key={sec.id} className={OPTION} title={sec.hint}>
              <input type="checkbox" className={CHOICE} checked={s.sections.includes(sec.id)} onChange={() => toggleSection(sec.id)}
                data-testid={`ts-export-include-${sec.id}`} />
              <span>{sec.label} <span className={HINT}>— {sec.short}</span></span>
            </label>
          ))}
          <div className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 dark:border-amber-700/60 dark:bg-amber-900/20">
            <label className={OPTION}>
              <input type="checkbox" className={CHOICE} checked={s.includeProjected} onChange={() => set({ includeProjected: !s.includeProjected })}
                data-testid="ts-export-projected" />
              <span>Include weeks not yet submitted, marked <strong>Projected</strong></span>
            </label>
            <p className="text-xs text-amber-900 dark:text-amber-200">
              Off = submitted weeks only, the frozen figures payroll pays from. Outstanding sheets are listed by name, no money.
            </p>
          </div>
        </Section>
        </>)}

        <Section legend="Format">
          <div className={ROW}>
            {/* The crew's default is PDF, so it leads there (the mock); the manager's order is unchanged. */}
            {crew && pdfChoice}
            <Radio name="ts-export-format" value="csv" current={s.format} onPick={(v) => set({ format: v })} testId="ts-export-format-csv">
              {CSV_LABEL} <span className={HINT}>one file</span>
            </Radio>
            {!crew && pdfChoice}
          </div>
        </Section>
      </div>
      {(problem || error) && (
        <p role={error ? 'alert' : 'status'} data-testid="ts-export-problem"
          className={`-mt-1 mb-3 text-sm ${error ? 'text-red-700 dark:text-red-300' : 'text-gray-600 dark:text-gray-400'}`}>
          {error || problem}
        </p>
      )}
    </ConfirmDialog>
  );
}
